import {
	Box,
	Button,
	Dialog,
	Grid,
	Stack,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { ResourceMappingModal } from './modalComponent';
import { ReactComponent as EditIcon } from '../../../assets/PepWise2.0/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/PepWise2.0/delete-img-new.svg';
import { ReactComponent as AddIcon } from '../../../assets/plus-icon.svg';
import {
	deleteSavedQuery,
	exportGetFinopsResourceMappingList,
	fetchSavedQueryList,
	getFinopsResource,
	getFinopsResourceMappingList,
	getProjectDetailsById,
} from '../../../service/service';
import { CustomizedDataGrid } from '../../customized_data_grid';
import LoaderFile from 'components/Loader/LoaderFile';
import LoaderComponent from 'components/Loader';
import { useLocation, useParams } from 'react-router-dom';
import 'react-day-picker/dist/style.css';
import PageHeader from 'components/Header/Page';
import DashboardIcon from '../../../assets/material-symbols_dashboard.svg';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import DataInfoSecondary from 'components/Widgets/DataInfoSecondary';
import { checkRole } from 'utils/roleUtils';
import eventEmitter from 'SnackBarEventEmitter';
import PepWiseQueryBuilder from 'components/QueryBuilder';
import RowTypography from 'components/Table/RowTypography';
import { CellExpanderFormatter } from 'components/cell_expander_formatter';
import { childInfoTransformData, formatNumberWithCommas } from 'utils/common-util';
import ProjectConfirmationDialog from './ProjectConfirmationDialog';
import PageWrapper from 'components/Common/PageWraper';
import colors from 'assets/theme/base/colors';
import { columnNames, fetchFieldOptionsHelper, fetchKeyFieldsData, formatAllocPercent } from './ResourceMappingHelper';

const renderRowTypography = (count: any, value: any, DBValue: any, idx: any, label: any, groupId?: any) => {
	const labelVal = count > 1 ? `${count} ${label}` : value?.split(',')[0];
	return count > 1 || value ? (
		<RowTypography
			sx={{
				backgroundColor: 'rgba(41, 155, 255, 0.15)',
				color: value !== DBValue ? `#FD9532 !important` : `${colors.table.text} !important`,
				fontWeight: 500,
				padding: '4px 10px',
				marginRight: '5px',
				borderRadius: '20px'
			}}
			key={idx}
			title={labelVal}
		>
			{labelVal}
		</RowTypography>
	) : (
		''
	);
};

const renderPepwiseSpecialCell = (
	column: any,
	row: any,
	idx: number,
	hasChildren: boolean,
	groupBy: string
): JSX.Element | null | '' => {
	if (column.key === 'PepwiseSector')
		return renderRowTypography(row.SectorCount, row.PepwiseSector, row.BusinessSector, idx, 'SECTORS', row.groupId);
	if (column.key === 'PepwiseProject')
		return renderRowTypography(row.ProjectCount, row.PepwiseProject, row.ProjectName, idx, 'PROJECTS', row.groupId);
	if (column.key === 'PepwiseProgram')
		return renderRowTypography(row.ProgramCount, row.PepwiseProgram, row.Program, idx, 'PROGRAMS', row.groupId);
	if (column.key === 'AllocPercent')
		return (
			<RowTypography
				sx={{
					fontWeight: 500,
					color: `${colors.table.text} !important`
				}}
				key={idx}
			>
				{formatAllocPercent(row.AllocPercent)}
			</RowTypography>
		);
	if (column.key === 'PepwiseEnvironment') {
		return (
			<RowTypography
				isSuccessor={!row.values}
				style={{ color: `${colors.table.text} !important` }}
				sx={{
					width: !hasChildren && idx === 0 ? 'calc(100% - 20px)' : 'auto',
					fontWeight: 500,
				}}
			>
				{row.PepwiseEnvironment ? row.PepwiseEnvironment.split(',')[0] : ''}
			</RowTypography>
		);
	}
	if (column.key === 'Resource' && hasChildren && groupBy.toLowerCase() === 'resourcegroup' && row.values.length > 0) {
		return (
			<RowTypography
				sx={{
					backgroundColor: 'rgba(41, 155, 255, 0.15)',
					color: `${colors.table.text} !important`,
					fontWeight: 500,
					padding: '4px 10px',
					marginRight: '5px',
					borderRadius: '20px'
				}}
				key={idx}
				title={row.values.length}
			>
				{row.values.length + ` RESOURCES`}
			</RowTypography>
		)
	}
	return null;
};

const pepwiseCellFormatter = (
	idx: number,
	groupBy: string,
	toggleSubRow: (id: string, respurceType: any) => void,
	toggleSubRowRG: (id: string, respurceType: any) => void,
) => {
	return ({ row, column, isCellSelected }: any): JSX.Element => {
		const isGroupBy = column.key.toLowerCase() === groupBy.toLowerCase();
		const hasChildren = (groupBy === 'ResourceGroup' && !!row.values) || (groupBy !== 'ResourceGroup' && (!!row.group_count && row.group_count >= 1));

		const specialCell = renderPepwiseSpecialCell(
			column,
			row,
			idx,
			hasChildren,
			groupBy
		);

		if (specialCell) {
			return specialCell;
		}

		// ---- Default render ----
		return (
			<Box display="flex">
				{idx === 0 && isGroupBy && hasChildren && row.isResource === false
					&& ((groupBy !== 'ResourceGroup' && row.group_count > 1) || (groupBy === 'ResourceGroup' && row.values.length > 0)) && (
						<CellExpanderFormatter
							isCellSelected={isCellSelected}
							expanded={row.isExpanded === true}
							onCellExpand={() => {
								groupBy === 'ResourceGroup' ? toggleSubRowRG(row.id, row.values) : toggleSubRow(row.id, row.values);
							}}
						/>
					)}
				{idx === 1 && column.key === 'Resource' && row.isResource && hasChildren && row.group_count > 1 && (
					<CellExpanderFormatter
						isCellSelected={isCellSelected}
						expanded={row.isExpanded === true}
						onCellExpand={() => {
							toggleSubRow(row.id, row.values);
						}}
					/>
				)}
				<Stack direction="row" alignItems="center" flexGrow={1} sx={{ width: '100%' }}>
					<RowTypography
						isSuccessor={!row.values}
						sx={{
							width: (!hasChildren && idx === 0) ? 'calc(100% - 20px)' : 'auto',
							flexGrow: 1,
							overflow: 'hidden',
							color: `${colors.table.text} !important`,
							fontWeight: 500,
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							maxWidth: 'calc(100% - 40px)',
							marginLeft: "8px",
							marginRight: "4px"
						}}
					>
						{row[column.key]}
					</RowTypography>
				</Stack>
			</Box>
		);
	};
};

const ProjectDetails = () => {
	interface Row {
		Id: string;
		[key: string]: any;
		Resource: string;
		Resourcetype: string;
		ResourceGroup: string;
		Environment: string;
		ProjectName: string;
		BillingInformation: string;
		Subscription: string;
		selected: boolean;
		DNAI_Value: string;
		ConsumedService: string;
		Allocpercent: string;
	}

	const { programId, projectId, program, project } = useParams();

	const [controller] = usePepwiseAuthController();
	const { role } = controller;

	const [, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};
	const handleMenuClose = (event: any) => {
		event.preventDefault();
		event.stopPropagation();
		setAnchorEl(null);
	};

	const [openModal, setOpenModal] = useState<any>({ isOpen: false });
	const [projectDetail, setProjectDetail] = useState<any>({});
	const [loading, setLoading] = useState(false);
	const [tableLoading, setTableLoading] = useState(false);
	const [linkLoading, setLinkLoading] = useState(false);
	const [isLoadingQuery, setIsLoadingQuery] = useState(true);
	const [resourceList, setResourceList] = useState<Row[]>([]);
	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
	const [filterInfo,] = useState([]);
	const [filterOptions,] = useState<any>({});
	const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState('100');
	const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);
	const [savedQuery, setSavedQuery] = useState<any>({});
	const location = useLocation();
	const { autoLoadOnMount } = location.state || {};
	const [shouldFetch, setShouldFetch] = useState(autoLoadOnMount || false);
	const [openConfirmationDialog, setOpenConfirmationDialog] = useState<any>({ isOpen: false, message: '', isUnlink: true, isCancel: false, isQuery: false })
	const abortControllerRef = useRef<AbortController | null>(null);
	const [isAbort, setIsAbort] = useState(false);

	const [valueField, setValueField] = useState<any>({});
	const [keyFields, setKeyFields] = useState({});
	const [isQueryPrefChange, setIsQueryPrefChange] = useState(false);
	const [, setTotalPages] = useState(0);
	const [isReapplied, setIsReapplied] = useState(false);
	const [hasResourceList, setHasResourceList] = useState(false);
	const [queryProps, setQueryProps] = useState({
		isSearch: false,
		query: ""
	});
	const queryPrefer: any = localStorage.getItem(`query-preference`);
	const queryPreferVal = queryPrefer ? JSON.parse(queryPrefer) : "Resource_flat";
	const [queryPreference, setQueryPreference] = useState(queryPreferVal);
	const queryPreferenceOptions = [
		{
			label: 'Tree List of Resource Groups',
			value: 'ResourceGroup_tree'
		},
		{
			label: 'Flat List of Resources',
			value: 'Resource_flat'
		},
		{
			label: 'Tree List of DBSQL Clusters',
			value: 'SqlEndpointId_tree'
		},
		{
			label: 'Tree List of Interactive Clusters',
			value: 'ClusterName_inttree'
		},
		{
			label: 'Tree List of Job Clusters',
			value: 'ClusterJobSource_jobtree'
		},
		{
			label: 'Blank Query',
			value: 'Blank_flatall'
		}
	];

	const parseFilter = (filterInfo: any, name?: string) => {
		// Call only filterable columns and reset the filtering in case of wrong/empty column
		const parsedFilter: any = {};
		Object.keys(filterInfo).forEach((key: any) => {
			if (key !== name && filterInfo[key] && filterInfo[key].length) {
				parsedFilter[key] = filterInfo[key];
			}
		});
		return parsedFilter;
	};

	const defaultProjectColumnNames = [
		'Resource',
		'Resource Type',
		'Resource Group',
		'Allocation %',
		'Pepwise Environment',
		'Pepwise Sector',
		'Pepwise Program',
		'Pepwise Project',
		'Subscription',
		'Sector',
		'Environment',
		'Program',
		'Consumed Service',
		'Location',
		'Billing Information',
		'Business Sector',
		'Project Name',
		'Project No',
		'OrgGrpCodeProjectCode',
		'Cluster Name',
		'Cluster JobSource',
		'Databricks Instance Pool Id',
		'DF Sectorname',
		'DF Projectcode',
		'DF Data Assetname',
		'Sql Endpoint Id',
		'Kubernetes pvc Namespace',
		'Meter Name',
		'D & AI',
	];

	async function expandRow(
		row: any,
		rowIndex: number,
		newRows: any[],
		id: string,
		resourceTypes: any
	) {
		let children: Row[] = [];
		let childRows: Row[] = [];
		const [groupBy, viewType] = queryPreference.split('_');
		const boxRows: any = localStorage.getItem('finopsGetResource');
		const storageRows = boxRows ? JSON.parse(boxRows) : [];
		let hasStrRow = false;
		storageRows.filter((strRow: any) => {
			if (strRow.id == row.id) {
				hasStrRow = true;
				newRows.splice(Number(rowIndex) + 1, 0, ...strRow.children);
				newRows[rowIndex] = { ...row, isExpanded: !row.isExpanded, children: strRow.children };
				const newUpdatedRows = [...newRows];
				constRows = newUpdatedRows
				setResourceList(newUpdatedRows);
			}
		});

		if (!hasStrRow) {
			let payload = resourceList.filter((data) => data.id === id)
			const groupId = payload[0].groupId;
			const res = await getFinopsResource({
				groupId,
				...((viewType === 'tree' || viewType === 'inttree' || viewType === 'jobtree') && {
					groupBy
				}),
				viewType
			});
			if (res.success) {
				Object.entries(res.data).forEach(([key, value]: any) => {
					childRows = childInfoTransformData(value);
				});
				children = childRows.map((x: any) => {
					return { ...x, isChildren: true };
				});
				newRows.splice(Number(rowIndex) + 1, 0, ...children);
				newRows[rowIndex] = { ...row, isExpanded: !row.isExpanded, children };
				const newUpdatedRows = [...newRows];

				constRows = newUpdatedRows
				setResourceList(newUpdatedRows);
				const stBoxRows: any = localStorage.getItem('finopsGetResource');
				const tempRows = [{
					id: row.id,
					Id: row.Id,
					groupId: row.groupId,
					children: children
				}]
				if (stBoxRows) {
					let tempStorageBoxRows = JSON.parse(stBoxRows)
					tempStorageBoxRows = tempStorageBoxRows.concat(tempRows);
					localStorage.setItem('finopsGetResource', JSON.stringify(tempStorageBoxRows))
				} else {
					localStorage.setItem('finopsGetResource', JSON.stringify(tempRows))
				}
			}
		}

		if (resourceTypes?.length > 0) {
			childRows = resourceTypes;
		}
		children = childRows.map((x: any) => {
			return { ...x, isChildren: true };
		});
		newRows.splice(Number(rowIndex) + 1, 0, ...children);
		newRows[rowIndex] = { ...row, isExpanded: true, children };
		row.isExpanded = true;
	}

	function collapseRow(row: any, rowIndex: number, newRows: any[]) {
		let children: Row[] = [];
		children = row.children ?? [];
		collapseChildren(children, newRows);
		newRows.splice(Number(rowIndex) + 1, children.length);
		newRows[rowIndex] = { ...row, isExpanded: false, children: [] };
		const newUpdatedRows = [...newRows];
		constRows = newUpdatedRows
		setResourceList(newUpdatedRows);
		localStorage.setItem('boxRows', JSON.stringify(newRows));
		row.isExpanded = false;
	}

	let constRows: any = [];
	async function toggleSubRow(id: string, resourceTypes: any) {
		const rows = constRows.length ? constRows : resourceList;
		const rowIndex = rows.findIndex((r: any) => r.id === id);
		if (rowIndex === -1) return;
		const row = rows[rowIndex];
		const newRows = [...rows];
		if (!row.isExpanded)
			await expandRow(row, rowIndex, newRows, id, resourceTypes);
		else
			collapseRow(row, rowIndex, newRows);
	}

	function toggleSubRowRG(id: string, resourceTypes: any) {
		setResourceList((prev) => {
			const rows = [...prev];
			const rowIndex = rows.findIndex((r: any) => r.id === id);
			const row = rows[rowIndex];
			const newRows = [...rows];
			let children: Row[] = [];
			let childRows: Row[] = [];
			if (!row.isExpanded) {
				if (resourceTypes?.length > 0) {
					childRows = resourceTypes;
				}
				children = childRows.map((x: any) => ({
					...x,
					isChildren: true
				}));
				newRows.splice(Number(rowIndex) + 1, 0, ...children);
				newRows[rowIndex] = { ...row, isExpanded: true, children };
				localStorage.setItem('boxRows', JSON.stringify(newRows));
				row.isExpanded = true;
			} else {
				children = row.children ?? [];
				collapseChildren(children, newRows);
				newRows.splice(Number(rowIndex) + 1, children.length);
				newRows[rowIndex] = { ...row, isExpanded: false, children: [] };
				localStorage.setItem('boxRows', JSON.stringify(newRows));
				row.isExpanded = false;
			}
			return newRows;
		});
	}

	const collapseChildren = (childrenToCollapse: Row[], newRows: Row[]) => {
		for (const child of childrenToCollapse) {
			const childIndex = newRows.findIndex((r) => r.id === child.id);
			if (child.isExpanded && child.values) {
				collapseChildren(child.values, newRows);
				newRows.splice(childIndex + 1, child.values.length);
			}
		}
	};

	function getColumns(): Array<any> {
		const [groupBy, viewType] = queryPreference.split('_');
		const columns = [];
		for (const value of columnNames) {
			const field = value.name;
			if (field.toLowerCase() !== groupBy.toLowerCase()) {
				columns.push(value);
			} else {
				columns.unshift(value);
			}
		}
		const columnArray: any = [
			...columns.map((col: any, idx: number) => ({
				key: col.name,
				name: col.label,
				editable: () => col.isEditable,
				resizable: true,
				frozen: ((col.name === groupBy || ['Resource'].includes(col.name)) && (viewType != 'flatall')),
				formatter: pepwiseCellFormatter(idx, groupBy, toggleSubRow, toggleSubRowRG),
				...(resourceList && resourceList.length > 0
					? { minWidth: col.name === 'actions' ? 20 : 200, maxWidth: col.name === 'actions' ? 20 : undefined }
					: { minWidth: col.name === 'actions' ? 20 : 180, maxWidth: col.name === 'actions' ? 20 : undefined })
			}))
		];
		return columnArray;
	}

	const getProjectDetails = async (isRefresh: any = false) => {
		setLoading(true);
		let result = await getProjectDetailsById({ projectId, programId });
		setLoading(false);
		const projectDetails = {
			...result.data,
			programName: decodeURIComponent(window.location.pathname.split('/')[6]),
		};
		setProjectDetail(projectDetails);
		if (isRefresh)
			projectMappedResourcesFunc(page, pageSize, parseFilter(filterInfo));
	};

	const passInfoToParent = (data: any) => {
		getProjectDetails(true);
	};

	const handleEditClose = (data: any) => {
		setOpenModal({
			isOpen: false
		});
	};

	const handleUnlinkProjectClick = (data: any) => {
		setOpenConfirmationDialog({
			...openConfirmationDialog,
			isOpen: true,
			data: data
		});
	};

	const handleUnlinkProjectSave = async (data: any) => {
		try {
			setLinkLoading(true);
			let result = await deleteSavedQuery(data.data.queryId, data.data.program, data.data.project, true);
			if (result.success) {
				handleConfirmationDialogClose(false);
			}
		} catch (err) {
			console.error(err);
		}
		finally {
			setLinkLoading(false);
		}
	}

	const handleConfirmationDialogClose = (isCancel: any = true) => {
		setOpenConfirmationDialog({
			...openConfirmationDialog,
			isOpen: false,
			isCancel: isCancel
		});
	};

	const handleSorting = (sortingInfo: any) => {
		projectMappedResourcesFunc(page, pageSize, sortingInfo);
	};

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: string) => {
		projectMappedResourcesFunc(value, pageSize, sortingInfo);
	};
	const handlepageSizeChange = (event: any) => {
		projectMappedResourcesFunc(page, event.target.value, sortingInfo);
	};

	async function projectMappedResourcesFunc(
		pageNumber: any,
		pageSize: any,
		sortingInfo: any,
		searchField?: any,
		searchText?: any
	) {
		const signal = abortControllerRef.current?.signal;
		const [groupBy, viewType] = queryPreference.split('_');
		const isTreeView = viewType === 'tree' || viewType === 'inttree' || viewType === 'jobtree';

		try {
			setTableLoading(true);
			setHasResourceList(false);
			const query = typeof queryProps.query === 'object' ? queryProps.query : { sql: '', params: [] };
			const res = await getFinopsResourceMappingList({
				page: pageNumber,
				size: pageSize,
				sortingInfo: sortingInfo,
				isProgram: false,
				params: query.params,
				sql: query.sql,
				searchField: 'detail',
				searchText: searchText,
				...(isTreeView && {
					groupBy
				}),
				viewType: viewType,
				signal
			});
			if (res.success) {
				const hasResults = res.data.result.length > 0;
				setHasResourceList(hasResults);
				setTableLoading(false);
				setResourceList(res.data.result);
				setPaginationData(res.data.page);
				setPage(res.data.page.currentPage);
				setPageSize(res.data.page.size);
				setSortingInfo(sortingInfo);
			}
		} catch (error) {
			console.error(error);
			const isCanceled = typeof error === 'object' && error !== null && 'code' in error && error.code === 'ERR_CANCELED';
			if (isCanceled) {
				console.warn('Request was canceled by user');
				eventEmitter?.emit('showSnackbar', 'Request was canceled', 'info');
				return;
			}
			if (typeof error === 'object' && error !== null && 'response' in error) {
				const err = error as { response: { status: number } };
				if (err.response.status === 503)
					eventEmitter.emit('showSnackbar', 'Service temporarily unavailable. Please try again later.', 'info');
				else if (err.response.status === 504)
					eventEmitter.emit('showSnackbar', 'Gateway Timeout', 'info');
			}
		} finally {
			setIsAbort(false);
			setTableLoading(false);
		}
	}

	// export
	const columnMap: { [key: string]: string } = {
		'Resource': 'Resource',
		'Resource Group': 'Resource Group',
		'Resource Type': 'Resource Type',
		'Allocation %': 'Allocation %',
		'Pepwise Sector': 'Pepwise Sector',
		'Pepwise Environment': 'Pepwise Environment',
		'Business Sector': 'Business Sector',
		'Project Name': 'Project Name',
		'Subscription': 'Subscription',
		'Consumed Service': 'Consumed Service',
		'Meter Category': 'Meter Category',
		'OrgGrpCodeProjectCode': 'Org Grp Code Project Code',
		'Billing Information': 'Billing Information',
		'Cluster Name': 'Cluster Name',
		'Cluster JobSource': 'Cluster Job Source'
	}
	async function exportTableData(
		pageNumber: any,
		pageSize: any,
		sortingInfo: any,
		prefColumns: any,
		searchField?: any,
		searchText?: any
	) {
		let formatType = 'EXCEL';
		let tableName = 'Finops_PrjtByResourceList';
		const exportColumns = prefColumns.map((prefCol: string) => columnMap[prefCol])
			.filter((col: string) => col !== null && col !== undefined);
		const [, viewType] = queryPreference.split('_');
		const query = typeof queryProps.query === 'object' ? queryProps.query : { sql: '', params: [] };
		if (project && program) {
			await exportGetFinopsResourceMappingList({
				exportColumns,
				formatType,
				page: pageNumber,
				pageSize,
				sortingInfo: sortingInfo,
				isProgram: true,
				params: query.params,
				sql: query.sql,
				searchField: searchField,
				searchText: searchText,
				viewType: viewType,
				tableName
			});
		}
	}

	const handleSearchData = (searchText: string) => {
		try {
			projectMappedResourcesFunc(page.toLocaleString(), pageSize, sortingInfo, 'quicksearch', searchText);
		} catch (error) {
			console.error(error);
		}
	};

	const handleAction = (module: string, type: string, data: any, e: any) => {
		e.preventDefault();
		e.stopPropagation();
		handleMenuClose(e);
		data = {
			...data,
			programName: decodeURIComponent(window.location.pathname.split('/')[6])
		};
		// debugger
		switch (type) {
			case 'edit':
				setOpenModal({
					isOpen: true,
					type: module,
					action: 'EDIT',
					from: 'view',
					data: module === 'Project' ? projectDetail : data
				});
				break;
			case 'delete':
				setOpenModal({
					isOpen: true,
					type: module,
					action: 'DELETE',
					from: 'delete',
					data: module === 'Project' ? projectDetail : data
				});
				break;
			default:
				break;
		}
	};

	const fetchFieldOptions = async (fieldKey: string) => {
		return fetchFieldOptionsHelper(
			fieldKey,
			queryPreference,
			setKeyFields
		);
	};

	const clearTableData = () => {
		setResourceList([]);
		setPaginationData({ totalElements: 0, totalPages: 0 });
		setPage(1);
		setPageSize('100');
		setTotalPages(0);
	};


	async function getSavedQueryList() {
		try {
			let queryPrefVal: any = queryPreference;
			if (localStorage.getItem(`query-preference`)) {
				const queryPref: any = localStorage.getItem(`query-preference`);
				queryPrefVal = JSON.parse(queryPref);
				setQueryPreference(queryPrefVal);
				localStorage.removeItem(`query-preference`);
			}
			const prgId = decodeURIComponent(window.location.pathname.split('/')[4]);
			const prjId = decodeURIComponent(window.location.pathname.split('/')[5]);
			let res = await fetchSavedQueryList('', queryPrefVal, prgId, prjId);
			if (res.success) {
				if (res.data.length > 0) {
					const formattedData = res.data.map((item: any) => ({
						...item,
						queryJson: JSON.parse(item.queryJson),
						selectedPaths: JSON.parse(item.selectedPaths),
						groupPaths: JSON.parse(item.groupPaths),
						param: item.param.split(','),
						cost: JSON.parse(item.costBreakdown)
					}));
					const firstQuery = formattedData[0];
					setSavedQuery(firstQuery);
					setIsReapplied(true);
					setQueryProps({
						isSearch: false,
						query: {
							sql: firstQuery.query,
							params: firstQuery.param
						} as any
					});
				}
				else {
					setSavedQuery({});
					setIsReapplied(true);
					setQueryProps({
						isSearch: false,
						query: ''
					});
				}
			}
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoadingQuery(false);
		}
	}

	const fetchFields = async () => {
		setLoading(true);
		const fieldsData = await fetchKeyFieldsData();
		setKeyFields(fieldsData);
		setLoading(false);
	};

	const fetchValue = async () => {
		setLoading(true);
		const newValueField = await fetchFieldOptions("Subscription");
		setValueField(newValueField);
		setLoading(false);
	};

	useEffect(() => {
		const activeId = document.getElementById('root');
		if (window.location.pathname.includes('/finops/resourcemapping/projects')) {
			if (activeId) {
				document.head.classList.add('theme-bg-blue');
				document.body.classList.add('theme-bg-blue');
			}
		}
		if (!openModal.isOpen && (!openConfirmationDialog.isOpen && !openConfirmationDialog.isCancel)) {
			fetchFields();
			fetchValue();
			getSavedQueryList();
			getProjectDetails();
			clearTableData();
		}
	}, [openModal, openConfirmationDialog]);

	useEffect(() => {
		const query = typeof queryProps.query === 'object' ? queryProps.query : { sql: '', params: [] };
		if (shouldFetch && query) {
			if (query.sql && query.sql !== '(1 = ?)') {
				projectMappedResourcesFunc(
					page,
					pageSize,
					sortingInfo,
					'quicksearch'
				);
				setShouldFetch(false);
			}
		}
	}, [queryProps, shouldFetch]);

	useEffect(() => {
		const handleProjectDetailsSearch = (event: any) => {
			const text = event.detail?.searchText || '';
			setPage(1);
			projectMappedResourcesFunc('1', pageSize, sortingInfo, 'quicksearch', text);
		};

		globalThis.addEventListener('projectDetailsSearch', handleProjectDetailsSearch);
		return () => {
			globalThis.removeEventListener('projectDetailsSearch', handleProjectDetailsSearch);
		};
	}, [pageSize, sortingInfo]);

	useEffect(() => {
		clearTableData();
		setIsQueryPrefChange(false);
	}, [isQueryPrefChange]);

	useEffect(() => {
		if (isReapplied)
			clearTableData();
	}, [isReapplied]);

	const getTruncatedValue = (value?: string, maxLength = 50): string => {
		if (!value) return '';
		return value.length > maxLength
			? `${value.substring(0, maxLength)}...`
			: value;
	};
	const LoadingValue = loading ? 'Loading...' : 'NA';

	return (
		<PageWrapper
			sx={{
				mt: { xs: '80px', sm: '50px', md: '46px' },
			}}
		>
			<PageHeader
				icon={DashboardIcon}
				headerSx={{ top: '78px !important' }}
				title={[
					{
						text: 'Programs',
						link: '/finops/resourcemapping/programs'
					},
					{
						text: decodeURIComponent(window.location.pathname.split('/')[6]),
						link: `/finops/resourcemapping/programs/${window.location.pathname
							.split('/')[4]
							.replace(/%20/g, ' ')}/${window.location.pathname.split('/')[6].replace(/%20/g, ' ')}`
					},
					{
						text: decodeURIComponent(window.location.pathname.split('/')[7])
					}
				]}
			>
				<Stack direction="row" gap={1} alignItems="center">
					<Button sx={{ '&.Mui-disabled': { color: '#ffffff9c !important' } }} variant="pepwiseSecondary" startIcon={<AddIcon />} disabled>
						ADD RESOURCES
					</Button>
				</Stack>
			</PageHeader>
			<Dialog open={openModal.isOpen} onClose={handleEditClose} fullWidth={true} maxWidth="md">
				<ResourceMappingModal
					handleEditClose={handleEditClose}
					programData={openModal}
					passInfoToParent={passInfoToParent}
				/>
			</Dialog>
			<Dialog open={openConfirmationDialog.isOpen} onClose={handleConfirmationDialogClose} fullWidth={true} maxWidth="md">
				<ProjectConfirmationDialog
					handleDialogClose={handleConfirmationDialogClose}
					projectData={openConfirmationDialog}
					handleSave={handleUnlinkProjectSave}
				/>
			</Dialog>
			<Box>
				{linkLoading && <LoaderFile />}
				<Grid container p={1} mt={1}>
					<Grid item xs={12} lg={12}>
						<DataInfoSecondary
							infoCards={[
								{
									label: 'Project Name',
									value: getTruncatedValue(projectDetail?.projectName),
									isLoading: loading,
									valueProps: {
										fontSize: '16px',
										fontWeight: 400,
										lineHeight: '26px',
										sx: {
											overflow: 'hidden',
											textOverflow: 'ellipsis'
										},
										title: projectDetail?.projectName
									},
									maxWidth: '24rem'
								},
								{
									label: 'Project Owner',
									value: getTruncatedValue(projectDetail?.projectOwner, 35),
									isLoading: loading,
									valueProps: {
										fontSize: '16px',
										fontWeight: 400,
										lineHeight: '26px',
										sx: {
											overflow: 'hidden',
											textOverflow: 'ellipsis'
										},
										title: projectDetail?.projectOwner
									},
									maxWidth: '24rem'
								},
								{
									label: 'Description',
									value: getTruncatedValue(projectDetail?.projectDescription),
									isLoading: loading,
									valueProps: {
										fontSize: '16px',
										fontWeight: 400,
										lineHeight: '26px',
										sx: {
											overflow: 'hidden',
											textOverflow: 'ellipsis'
										},
										title: projectDetail?.projectDescription
									},
									maxWidth: '24rem',
								},
								{
									label: 'Total Cost (YTD)',
									value: projectDetail.finalCost && projectDetail.finalCost !== 'null' ?
										`$${formatNumberWithCommas(projectDetail.finalCost)}` : '-',
									isLoading: loading,
									valueProps: {
										fontSize: '16px',
										fontWeight: 400,
										lineHeight: '26px'
									},
									maxWidth: '24rem',
									hasTooltip: true
								},
							]}
							infoTooltip={{
								titleValues: [
									{
										label: 'Project Owner',
										value: projectDetail?.projectOwner ? projectDetail.projectOwner : LoadingValue
									},
									{
										label: 'Description',
										value: projectDetail?.projectDescription
											? projectDetail.projectDescription
											: LoadingValue
									}
								],
								dateValues: [
									{ label: 'Date Created', value: projectDetail?.createdAt ?? 'NA' },
									{ label: 'Last Updated', value: projectDetail?.updatedAt ?? 'NA' }
								]
							}}
							infoOptionMenu={{
								onClick: handleClick,
								onClose: handleMenuClose,
								menuItems: !projectDetail.Clarity_Project_Id
									? [
										{
											key: 'edit',
											label: 'Edit',
											icon: <EditIcon />,
											color: '#000000',
											onMenuClick: (e) => handleAction('Project', 'edit', {}, e)
										},
										{
											key: 'delete',
											label: 'Delete',
											icon: <DeleteIcon />,
											color: '#000000',
											onMenuClick: (e) => handleAction('Project', 'delete', {}, e)
										}
									]
									: [
										{
											key: 'delete',
											label: 'Delete',
											icon: <DeleteIcon />,
											onMenuClick: (e) => handleAction('Project', 'delete', {}, e)
										}
									]
							}}
							bgColor="#FBFDFF"
							isAdmin={isAllowed ? true : false}
							sx={{ boxShadow: '4px 8px 54px 0px #00000014', color: '#000000' }}
						/>
					</Grid>
				</Grid>
				<Grid item xs={12} p={1}>
					{!isLoadingQuery && <PepWiseQueryBuilder
						valueField={valueField}
						fields={keyFields}
						setQueryProps={setQueryProps}
						queryPreference={queryPreference}
						setQueryPreference={setQueryPreference}
						queryPreferenceOptions={queryPreferenceOptions}
						loadOptionsForField={fetchFieldOptions}
						fieldValueOptionsCallback={getFinopsResourceMappingList}
						setTotalPages={setTotalPages}
						sortingInfo={sortingInfo}
						setSortingInfo={setSortingInfo}
						setIsQueryPrefChange={setIsQueryPrefChange}
						setIsReapplied={setIsReapplied}
						isReapplied={isReapplied}
						isProjectDetails={true}
						savedQuery={savedQuery}
						setSavedQuery={setSavedQuery}
						hasResourceList={hasResourceList}
						setHasResourceList={setHasResourceList}
						setShouldFetch={setShouldFetch}
						handleUnlinkProject={handleUnlinkProjectClick}
						abortControllerRef={abortControllerRef}
						setIsAbort={setIsAbort}
						isAbort={isAbort}
					/>}
				</Grid>
				<Grid item xs={12} p={1}>
					{!tableLoading ? (
						<CustomizedDataGrid
							title={'Resources'}
							tableColumns={getColumns()}
							tableData={resourceList}
							tableType="resource-details"
							defaultColumnNames={defaultProjectColumnNames}
							filterDataOptions={filterOptions}
							onSorting={handleSorting}
							currSorting={sortingInfo}
							currFilter={filterInfo}
							page={page}
							pageSize={pageSize}
							paginationData={paginationData}
							listLength={resourceList.length}
							handlepageSizeChange={handlepageSizeChange}
							handlePageChange={handlePageChange}
							isDateRangeFilter={false}
							isDisabled={false}
							showExport={true}
							isFetching={tableLoading}
							isShowMoreButton={true}
							childRowClass={true}
							exportTableData={(prefColumns: any) => exportTableData(page, pageSize, sortingInfo, prefColumns)}
							isSortIcon={true}
							isLighterTheme={true}
						/>
					) : (
						<LoaderComponent />
					)}
				</Grid>
			</Box>
		</PageWrapper>
	);
};

export default ProjectDetails;

